import {
	formatClock,
	formatDayMonth,
	formatRelativeStart,
	formatWeekday,
	festivalDayKey,
	getCountdown,
	getFestivalPhase,
	type ProgramEvent,
} from '#app/domains/program/pure'
import { Odometer } from '../../atoms/Odometer.tsx'

const when = (event: ProgramEvent, now: Date) => {
	const sameDay = festivalDayKey(event.date) === festivalDayKey(now)
	return sameDay
		? `i kveld kl. ${formatClock(event.date)}`
		: `${formatWeekday(event.date)} kl. ${formatClock(event.date)}`
}

/**
 * Days, hours, minutes and seconds to `target` on rolling digits. The
 * digits are decorative; assistive tech gets one sentence that only
 * changes once a minute.
 */
export function Countdown({
	target,
	now,
	label,
}: {
	target: Date | string
	now: Date
	label: string
}) {
	const { days, hours, minutes, seconds } = getCountdown(target, now)
	const units = [
		{ value: days, label: days === 1 ? 'dag' : 'dagar' },
		{ value: hours, label: 'timar' },
		{ value: minutes, label: 'min' },
		{ value: seconds, label: 'sek' },
	]
	return (
		<>
			<p className="hk-status-label" aria-hidden="true">
				{label}
			</p>
			<div className="hk-countdown">
				{units.map((unit) => (
					<div key={unit.label} className="hk-countdown-unit">
						<Odometer
							value={unit.value}
							minDigits={2}
							className="hk-countdown-value"
						/>
						<span className="hk-countdown-name" aria-hidden="true">
							{unit.label}
						</span>
					</div>
				))}
			</div>
			<p className="sr-only">
				{label} {days} {days === 1 ? 'dag' : 'dagar'}, {hours}{' '}
				{hours === 1 ? 'time' : 'timar'} og {minutes} minutt.
			</p>
		</>
	)
}

/**
 * The hero's live line: a rolling countdown to the first concert, what just
 * started and what's next during the festival, a thank-you afterwards.
 * Everything derives from the concerts' start times and `now`.
 */
export function FestivalStatus({
	events,
	now,
}: {
	events: ProgramEvent[]
	now: Date
}) {
	const status = getFestivalPhase(events, now)

	if (status.phase === 'empty') {
		return (
			<div className="hk-status">
				<p className="hk-status-label">Programmet kjem snart</p>
			</div>
		)
	}

	if (status.phase === 'before') {
		return (
			<div className="hk-status">
				<Countdown
					target={status.first.date}
					now={now}
					label="Første konsert om"
				/>
				<p className="hk-status-meta">
					<a href={`#event-${status.first.id}`} className="hk-status-link">
						{formatWeekday(status.first.date)}{' '}
						{formatDayMonth(status.first.date)} kl.{' '}
						{formatClock(status.first.date)} · {status.first.title}
					</a>
				</p>
			</div>
		)
	}

	if (status.phase === 'during') {
		const { current, next } = status
		return (
			<div className="hk-status" data-live="">
				{current ? (
					<p className="hk-status-now">
						<span className="hk-live-dot" aria-hidden="true" />
						<span>
							Starta kl. {formatClock(current.date)}:{' '}
							<a href={`#event-${current.id}`} className="hk-status-link">
								{current.title}
							</a>
						</span>
					</p>
				) : (
					<p className="hk-status-label">Festivalen er i gang</p>
				)}
				{next ? (
					<p className="hk-status-meta">
						Neste {formatRelativeStart(next.date, now)}:{' '}
						<a href={`#event-${next.id}`} className="hk-status-link">
							{next.title}
						</a>{' '}
						<span className="hk-status-when">{when(next, now)}</span>
					</p>
				) : (
					<p className="hk-status-meta">Siste konsert for i år — kos deg!</p>
				)}
			</div>
		)
	}

	return (
		<div className="hk-status">
			<p className="hk-status-label">Takk for i år!</p>
			<p className="hk-status-meta">
				Takk til alle vertane som opna dørene sine, og til alle som kom og fylte
				stovene med musikk.
			</p>
		</div>
	)
}
